/* eslint require-jsdoc: 'error', valid-jsdoc: ["error", { "requireReturn": false }] */
import Column from '@fusioncharts/charts/src/dataset/column';
import { pluck, pluckNumber, getValidValue, extend2, getDashStyle,
  parseUnsafeString, preDefStr, getFirstValue, getFirstColor, BLANKSTRING } from '@fusioncharts/core/src/lib';
import { _parseToolText as __parseToolText } from './candlestick';

let colorStrings = preDefStr.colors,
  COLOR_B90000 = colorStrings.B90000,
  DASH_DEF = 'none',
  SHOWSHADOW = 'showShadow',
  COLOR_FFFFFF = colorStrings.FFFFFF;

/**
 * Class def for bull candles chart API
 * This dataset is reponsible for drawing candle depending on open and close value
 */
class Volume extends Column {
  /**
    * Function for parsing all the attributes and value given by the user at chart,dataset and set level.
    * This function is called once from the init() function of the Column class.
    * @param  {Object} datasetJSON JSON for dataset configurations
    * @return {boolean} Indicates whether the configuration was successful or not
    */
  configureAttributes (datasetJSON) {
    if (!datasetJSON) {
      return false;
    }
    this.trimData(datasetJSON);
    this.config.JSONData = datasetJSON;

    var dataset = this,
      conf = dataset.config,
      JSONData = dataset.config.JSONData,
      dataArr = JSONData.data || [],
      chartAttr = dataset.getFromEnv('chart-attrib'),
      chartConfig = dataset.getFromEnv('chartConfig'),
      dataLength = dataArr.length, // eslint-disable-line good-practices/no-single-usage-variable
      colorM = dataset.getFromEnv('color-manager'),
      setColor,
      setAlpha,
      pointShadow,
      index,
      dataObj,
      open,
      close,
      volume,
      x,
      borderAlpha,
      numberFormatterAttrs,
      plotLineDashLen,
      plotLineDashGap,
      // Candle stick properties.
      // Bear fill and border color - (Close lower than open)
      bearBorderColor = conf.bearBorderColor = // eslint-disable-line good-practices/no-single-usage-variable
                    getFirstColor(pluck(chartAttr.bearbordercolor, chartConfig.vplotbordercolor, COLOR_B90000)),
      bearFillColor = conf.bearFillColor = // eslint-disable-line good-practices/no-single-usage-variable
                    getFirstColor(pluck(chartAttr.bearfillcolor, COLOR_B90000)),
      // Bull fill and border color - Close higher than open
      bullBorderColor = conf.bullBorderColor = // eslint-disable-line good-practices/no-single-usage-variable
                    getFirstColor(pluck(chartAttr.bullbordercolor, chartConfig.vplotbordercolor,
                      colorM.getColor('canvasBorderColor'))),
      bullFillColor = conf.bullFillColor = // eslint-disable-line good-practices/no-single-usage-variable
                getFirstColor(pluck(chartAttr.bullfillcolor,
                  COLOR_FFFFFF)),
      showVPlotBorder = pluckNumber(chartConfig.showplotborder, 1), // eslint-disable-line good-practices/no-single-usage-variable
      // VPlotBorder is border properties for the volume chart.
      vPlotBorderThickness = showVPlotBorder ? pluckNumber(chartAttr.vplotborderthickness, 1) : 0,
      dataStore,
      setData,
      config,
      plotSpacePercent,
      numberFormatter = dataset.getFromEnv('number-formatter'),
      yMax = -Infinity,
      yMin = +Infinity,
      xMax = -Infinity,
      xMin = +Infinity;
    dataset.setState('visible', pluckNumber(JSONData.visible, 1) === 1);
    if (dataset.getState('visible') === true) {
      dataset._conatinerHidden = false;
    } else {
      dataset._containerHidden = true;
    }

    conf.minAbsNonZeroValue = Infinity;
    conf.minAbsNonZeroData = {};

    conf.parentYAxis = 1;
    conf.volumeToolText = getValidValue(parseUnsafeString(pluck(JSONData.volumetooltext,
      chartAttr.volumetooltext, chartAttr.plottooltext), false));
    // Dataset seriesname
    conf.name = getValidValue(JSONData.seriesname);
    conf.showTooltip = pluckNumber(chartAttr.showtooltip, 1);
    conf.showShadow = pluckNumber(chartAttr.showshadow, colorM.getColor(SHOWSHADOW));
    conf.maxColWidth = Math.abs(pluckNumber(
      pluck(chartAttr.maxcolwidth), 50)) || 1;
    plotSpacePercent = Math.max(pluckNumber(chartAttr.plotspacepercent, 20) % 100, 0); // eslint-disable-line good-practices/no-single-usage-variable
    conf.plotSpacePercent = conf.groupPadding = plotSpacePercent / 200;
    conf.plotborderthickness = vPlotBorderThickness;
    dataStore = dataset.components.data = dataset.components.data || (dataset.components.data = []);
    numberFormatterAttrs = extend2(extend2({}, chartAttr), { // eslint-disable-line good-practices/no-single-usage-variable
      sforcedecimals: getFirstValue(chartAttr.forcevdecimals,
        chartAttr.forcedecimals),
      sforceyaxisvaluedecimals: getFirstValue(
        chartAttr.forcevyaxisvaluedecimals,
        chartAttr.forceyaxisvaluedecimals),
      syaxisvaluedecimals: getFirstValue(
        chartAttr.vyaxisvaluedecimals,
        chartAttr.yaxisvaluedecimals),
      sformatnumber: getFirstValue(chartAttr.vformatnumber,
        chartAttr.formatnumber),
      sformatnumberscale: getFirstValue(
        chartAttr.vformatnumberscale,
        chartAttr.formatnumberscale, '1'),
      sdefaultnumberscale: getFirstValue(
        chartAttr.vdefaultnumberscale,
        chartAttr.defaultnumberscale),
      snumberscaleunit: getFirstValue(
        chartAttr.vnumberscaleunit, chartAttr.numberscaleunit),
      vnumberscalevalue: getFirstValue(
        chartAttr.vnumberscalevalue,
        chartAttr.numberscalevalue),
      sscalerecursively: getFirstValue(
        chartAttr.vscalerecursively,
        chartAttr.scalerecursively),
      smaxscalerecursion: getFirstValue(
        chartAttr.vmaxscalerecursion,
        chartAttr.maxscalerecursion),
      sscaleseparator: getFirstValue(chartAttr.vscaleseparator,
        chartAttr.scaleseparator),
      snumberprefix: getFirstValue(chartAttr.vnumberprefix,
        chartAttr.numberprefix),
      snumbersuffix: getFirstValue(chartAttr.vnumbersuffix,
        chartAttr.numbersuffix),
      sdecimals: getFirstValue(chartAttr.vdecimals,
        chartAttr.decimals)
    });
    plotLineDashLen = conf.plotLineDashLen = pluckNumber(chartAttr.plotlinedashlen, 5); // eslint-disable-line good-practices/no-single-usage-variable
    plotLineDashGap = conf.plotLineDashGap = pluckNumber(chartAttr.plotlinedashgap, 4); // eslint-disable-line good-practices/no-single-usage-variable

    dataset.getFromEnv('number-formatter').configure(numberFormatterAttrs);

    // Iterate through all level data
    for (index = 0; index < dataLength; index += 1) {
      // Individual data obj
      // for further manipulation
      setData = dataArr[index];
      dataObj = dataStore[index];
      if (!dataObj) {
        dataObj = dataStore[index] = {};
      }
      !dataObj.config && (dataObj.config = {});
      !dataObj.graphics && (dataObj.graphics = {});
      config = dataObj.config;
      if (setData && !setData.vline) {
        open = config.open = numberFormatter.getCleanValue(setData.open);
        close = config.close = numberFormatter.getCleanValue(setData.close);
        volume = config.volume = numberFormatter.getCleanValue(setData.volume, true);
        config.high = numberFormatter.getCleanValue(setData.high);
        config.low = numberFormatter.getCleanValue(setData.low);
        x = config.x = numberFormatter.getCleanValue(setData.x);

        yMax = Math.max(yMax, volume);
        yMin = Math.min(yMin, volume);
        xMax = Math.max(xMax, x);
        xMin = Math.min(xMin, x);
        // valueText = parseUnsafeString(getValidValue(setData.valuetext, BLANK));

        config.borderColor = getFirstColor(pluck(setData.bordercolor, close < open ? bearBorderColor
          : bullBorderColor));
        setAlpha = pluckNumber(setData.alpha, 100);
        borderAlpha = pluckNumber(chartConfig.vplotborderalpha, setData.alpha, 100);
        setColor = getFirstColor(pluck(setData.color, close < open ? bearFillColor
          : bullFillColor));
        // Set alpha of the shadow
        pointShadow = {
          opacity: conf.showShadow ? (setAlpha / 100) : 0
        };
        config.color = setColor;
        config.alpha = setAlpha;
        config.setLink = setData.link;
        config.borderWidth = vPlotBorderThickness;
        config.borderAlpha = borderAlpha;
        config.y = volume;
        config.colorArr = [{
          color: config.color,
          alpha: config.alpha
        },
        {
          color: config.borderColor,
          alpha: config.borderAlpha
        }];
        config.toolText = dataset._parseToolText(index);
        config.toolTipValue = BLANKSTRING;
        config.setValue = config._y = config.y;
        if (volume !== 0 && conf.minAbsNonZeroValue > Math.abs(volume)) {
          conf.minAbsNonZeroValue = Math.abs(volume);
          conf.minAbsNonZeroData = config;
        }
        config.dashed = pluckNumber(conf.plotLineDashed, setData.dashed, 0);
        config.plotBorderDashStyle = pluckNumber(setData.dashed)
          ? getDashStyle(plotLineDashLen, plotLineDashGap) : DASH_DEF;
        x = (x !== null) ? x : index + 1;
        config._x = x;
        config.shadow = pointShadow;
      }
    }
    conf.defaultPadding = {
      left: 0,
      right: 0
    };
    conf.yMax = yMax;
    conf.yMin = yMin;
    conf.xMax = xMax;
    conf.xMin = xMin;
    dataset.setState('dirty', true);
  }
  /**
   * parse tooltext from the dataset json
   * @param {number} i index of data
   * @return {string} tooltext
   */
  _parseToolText (i) {
    return __parseToolText.call(this, i);
  }
  /**
   * fetches data indices wrt. the x pos value
   * @param {number} x  x pos value
   * @return {Array}    contains indices
   */
  getPlotIndices (x) {
    var dataset = this,
      i,
      minX = Math.floor(x), // eslint-disable-line good-practices/no-single-usage-variable
      maxX = Math.ceil(x), // eslint-disable-line good-practices/no-single-usage-variable
      data,
      returnIndices = [],
      sortedData = dataset.config.JSONData && dataset.config.JSONData.data;

    for (i = sortedData.length; i--;) {
      data = sortedData[i];
      if (data.x >= minX && data.x <= maxX) {
        returnIndices.push(i);
      }
    }
    return returnIndices;
  }
  /**
   * Function that retunr the nearest plot details
   * @param {number} chartXVal x-axis position of the mouse cordinate
   * @param {number} chartYVal x-axis position of the mouse cordinate
   * @return {Object} return an object with details of nearest polt and whether it is hovered or not
   */
  _getHoveredPlot (chartXVal, chartYVal) {
    var dataset = this,
      chartX = chartXVal,
      chartY = chartYVal,
      xAxis = dataset.getFromEnv('xAxis'),
      x,
      i,
      hoveredInfo,
      pXs,
      pX;

    chartX += xAxis.getTranslation();
    x = xAxis.getValue(chartX);
    pXs = dataset.getPlotIndices(x);
    // Checking for overlap between two cosecutive column plots along x-axis
    for (i = pXs.length - 1; i > -1; i--) {
      pX = pXs[i];
      hoveredInfo = (pX - x > 0 ? dataset._checkPointerOverColumn(pX, chartX, chartY) ||
        dataset._checkPointerOverColumn(pX - 1, chartX, chartY)
        : dataset._checkPointerOverColumn(pX + 1, chartX, chartY) ||
              dataset._checkPointerOverColumn(pX, chartX, chartY));
      if (hoveredInfo) {
        break;
      }
    }
    return hoveredInfo;
  }
  /**
   * Function to get the maximum and minimum from dataset
   * @return {Object}     contains max, min value
   */
  getDataLimits () {
    var conf = this.config;
    return {
      max: conf.yMax,
      min: conf.yMin,
      xMax: conf.xMax,
      xMin: conf.xMin
    };
  }
  /**
   * Adds dataset Specific event Arguments
   * @param {Object} _dataObj dataObject
   */
  // eslint-disable-next-line class-methods-use-this
  addDatasetSpecificEvtArgs (_dataObj) {
    let dataObj = _dataObj,
      config = dataObj.config,
      trackerConfig = dataObj.trackerConfig,
      eventArgs = trackerConfig.eventArgs;
    Object.assign(eventArgs, {
      open: config.open,
      close: config.close,
      high: config.high,
      low: config.low,
      volume: config.volume,
      alpha: config.alpha,
      x: config.x,
      displayValue: config.displayValue,
      color: config.color,
      borderColor: config.borderColor,
      dashed: config.dashed
    });
  }
  /**
   * calculate min and max position to scroll
   */
  calculateScrollRange () {
    let dataset = this,
      conf = dataset.config;
    conf.scrollMinVal = 0;
    conf.scrollMaxVal = dataset.components.data && dataset.components.data.length;

    // Since panning and data skipping are not applicable for scatter and bubble charts, the labels'
    // minimum and maximum index to be drawn is the same as the plots' minimum and maximum index.
    conf.scrollMinValForLabel = conf.scrollMinVal;
    conf.scrollMaxValForLabel = conf.scrollMaxVal;
  }
  /**
    * This function overrides drawlabel method
    * it prevents the label drawing
    */
  // eslint-disable-next-line
  drawLabel () {
  }
  /**
   * Returns the name of the component
   *
   * @return {string} The name of the component
   */
  getName () {
    return 'volume';
  }
}

export default Volume;
